<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { QuizStateEnum, useVocabQuiz } from "@/common/composables/use-vocab-quiz";
import { randomNumber } from "@/common/utils";
import ImageResponsive from "@/components/ImageResponsive.vue";
import QuizAchievementProgress from "@/components/vocabularyTrainer/QuizAchievementProgress.vue";
import QuizCheckpoint from "@/components/vocabularyTrainer/QuizCheckpoint.vue";
import QuizEnd from "@/components/vocabularyTrainer/QuizEnd.vue";
import QuizProgress from "@/components/vocabularyTrainer/QuizProgress.vue";
import QuizQuestions from "@/components/vocabularyTrainer/QuizQuestions.vue";

const { shareCode } = useRoute().params;

const {
  rounds,
  currentRound,
  currentRoundIndex,
  checkpoints,
  quizState,
  wrongAnswers,

  initializeQuiz,
  nextRound,
  skipRound,
  validateAnswer,
  enableQuiz
} = useVocabQuiz();

const showGame = computed(
  () =>
    [QuizStateEnum.Prompted, QuizStateEnum.Answered].includes(quizState.value) &&
    currentRound.value !== undefined
);
const showQuizCheckpoint = computed(() => quizState.value === QuizStateEnum.Checkpoint);
const showQuizEnd = computed(() => quizState.value === QuizStateEnum.Finished);
const showAchievementProgress = computed(
  () => quizState.value === QuizStateEnum.AchievementProgress
);

const totalRounds = computed(() => rounds.value.length);
const finishedRound = computed(() =>
  currentRoundIndex?.value !== undefined ? currentRoundIndex.value : 0
);

const encouragementNumber = ref(randomNumber(4));
watch(showQuizCheckpoint, (v) => {
  if (v) {
    encouragementNumber.value += 1;
  }
});

const onAnswer = () => nextRound(2000);

onMounted(async () => {
  await initializeQuiz();
  enableQuiz();
});
</script>

<template>
  <main class="d-flex flex-column">
    <div class="quiz-grid">
      <!-- :key prop to re-render components when the round changes - ensuring fresh state every time -->
      <QuizQuestions
        v-if="showGame"
        :key="currentRoundIndex"
        :answer-validator="validateAnswer"
        :current-questions="currentRound?.questions"
        :current-answers="currentRound?.userAnswers"
        :question-type="currentRound?.questionType"
        :quiz-state="quizState"
        :timeout="2000"
        @answer="onAnswer()"
        @skip="skipRound()"
        @enable-quiz="enableQuiz()"
      >
        <template #quiz-prompt-image>
          <ImageResponsive
            img-class="quiz-prompt-image"
            src="/assets/img/vocab/game/boy-and-girl@1x.png"
            presentational
          />
        </template>
      </QuizQuestions>
      <QuizCheckpoint
        v-else-if="showQuizCheckpoint"
        :encouragement-number="encouragementNumber"
        @next="quizState = QuizStateEnum.Prompted"
      />
      <QuizEnd
        v-else-if="showQuizEnd"
        :wrong-answers="wrongAnswers"
        @next="quizState = QuizStateEnum.AchievementProgress"
      />
      <QuizAchievementProgress
        v-else-if="showAchievementProgress"
        :return-location="{
          name: 'vocab.list',
          params: { shareCode }
        }"
      />

      <div class="quiz-footer">
        <div class="container">
          <QuizProgress
            :is-finished="showQuizEnd"
            :total="totalRounds"
            :finished-round="finishedRound"
            :checkpoints="checkpoints"
            :checkpoint-active="showQuizCheckpoint"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.quiz-grid {
  display: grid;
  grid-template-rows: auto 1fr auto;
  flex: 1 0 0;
  height: 100%;
  background: @colors[@white];
}

:deep(.quiz-prompt-image) {
  max-height: 220px;
  margin-top: 8px;

  #media-breakpoint-up(xxl, {
    max-height: 245px;
    margin-top: 16px;
  });
}

.quiz-footer {
  padding: 30px 15px;
  border-top: 1px solid rgb(@theme-colors[@grey][50]);

  #media-breakpoint-up(md, {
    padding: 38px 0 52px;
  });
}
</style>
